import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { KeycloakService, ModalRef, ModalService } from '@atsystems/ui';
import { driver, type Driver } from 'driver.js';
import { Observable, Subject, defer, filter, from, isObservable, map, of, switchMap, take, takeUntil, timer } from 'rxjs';
import {
  PRODUCT_TOUR_CLOSE_MODAL_ACTION,
  PRODUCT_TOUR_DEFAULT_WAIT_TIMEOUT_MS,
  PRODUCT_TOUR_MOBILE_BREAKPOINT,
  PRODUCT_TOUR_OVERLAY_OPACITY,
  PRODUCT_TOUR_POLL_INTERVAL_MS,
  PRODUCT_TOUR_QUERY_PARAM,
} from './product-tour.consts';
import { TourContext, TourDefinition, TourStep, TourValue } from './product-tour.types';

@Injectable({ providedIn: 'root' })
export class ProductTourService {
  private router = inject(Router);
  private modalService = inject(ModalService);
  private keycloakService = inject(KeycloakService);

  private tours = new Map<string, TourDefinition>();
  private actions = new Map<string, () => void | Observable<unknown> | Promise<unknown>>();

  private driverInstance: Driver | null = null;
  private currentTour: TourDefinition | null = null;
  private currentContext: TourContext = {};
  private currentModalRef: ModalRef | null = null;
  private currentModalComponent: Parameters<ModalService['open']>[0] | null = null;
  private modalClosedByTour = false;
  private stop$ = new Subject<void>();

  private _isActive = signal(false);
  private _currentStepIndex = signal(0);
  private _isProcessing = signal(false);

  public readonly isActive = this._isActive.asReadonly();
  public readonly currentStepIndex = this._currentStepIndex.asReadonly();
  public readonly isProcessing = this._isProcessing.asReadonly();
  public readonly currentTourKey = computed(() => (this._isActive() ? (this.currentTour?.key ?? null) : null));

  constructor() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        take(1),
      )
      .subscribe(() => this.consumeQueryParam());
  }

  public registerTour(definition: TourDefinition): void {
    this.tours.set(definition.key, definition);
  }

  public hasTour(key: string): boolean {
    return this.tours.has(key);
  }

  public canAccessTour(tour: TourDefinition | string | undefined): boolean {
    const def = typeof tour === 'string' ? this.tours.get(tour) : tour;
    if (!def) return false;
    if (def.includes?.length) return def.includes.some(child => this.canAccessTour(child));
    return !def.roles?.length || def.roles.every(group => this.keycloakService.hasResourceRole(group));
  }

  private resolveSteps(tour: TourDefinition): TourStep[] {
    if (!tour.includes?.length) return tour.steps;
    const childSteps = tour.includes
      .filter(child => this.canAccessTour(child))
      .flatMap(child => this.resolveSteps(child));
    return [...tour.steps, ...childSteps];
  }

  public openInNewTab(tourKey: string, basePath = '/'): void {
    if (window.innerWidth <= PRODUCT_TOUR_MOBILE_BREAKPOINT) return;
    if (!this.hasTour(tourKey)) {
      console.warn(`[ProductTour] Tour "${tourKey}" is not registered`);
      return;
    }
    if (!this.canAccessTour(tourKey)) return;
    const url = this.router.serializeUrl(
      this.router.createUrlTree([basePath], { queryParams: { [PRODUCT_TOUR_QUERY_PARAM]: tourKey } }),
    );
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private consumeQueryParam(): void {
    const url = new URL(window.location.href);
    const tourKey = url.searchParams.get(PRODUCT_TOUR_QUERY_PARAM);
    if (!tourKey) return;

    url.searchParams.delete(PRODUCT_TOUR_QUERY_PARAM);
    window.history.replaceState({}, '', url.toString());

    if (!this.hasTour(tourKey)) {
      console.warn(`[ProductTour] Tour "${tourKey}" from URL is not registered`);
      return;
    }
    if (!this.canAccessTour(tourKey)) return;
    this.start(tourKey);
  }

  public registerAction(key: string, fn: () => void | Observable<unknown> | Promise<unknown>): void {
    this.actions.set(key, fn);
  }

  public unregisterAction(key: string): void {
    this.actions.delete(key);
  }

  public start(tourKey: string, context: TourContext = {}): void {
    if (window.innerWidth <= PRODUCT_TOUR_MOBILE_BREAKPOINT) return;
    if (this._isActive()) this.stop();

    const tour = this.tours.get(tourKey);
    if (!tour) {
      console.warn(`[ProductTour] Tour "${tourKey}" is not registered`);
      return;
    }
    if (!this.canAccessTour(tour)) return;

    const steps = this.resolveSteps(tour);
    if (!steps.length) return;

    this.currentTour = { ...tour, steps };
    this.currentContext = context;
    this._currentStepIndex.set(0);
    this._isActive.set(true);

    this.prepareStep$(this.currentTour.steps[0], 0)
      .pipe(takeUntil(this.stop$))
      .subscribe({
        next: () => this.initDriver(this.currentTour!),
        error: err => {
          console.warn('[ProductTour] Failed to prepare first step:', err);
          this.stop();
        },
      });
  }

  public stop(): void {
    this.stop$.next();

    if (this.driverInstance) {
      const instance = this.driverInstance;
      this.driverInstance = null;
      if (instance.isActive()) instance.destroy();
    }

    this.closeCurrentModal();

    this.currentTour = null;
    this.currentContext = {};
    this._isActive.set(false);
    this._isProcessing.set(false);
    this._currentStepIndex.set(0);
  }

  private initDriver(tour: TourDefinition): void {
    this.driverInstance = driver({
      showProgress: tour.steps.length > 1,
      allowClose: true,
      overlayOpacity: PRODUCT_TOUR_OVERLAY_OPACITY,
      steps: tour.steps.map(step => ({
        element: this.resolve(step.element),
        popover: {
          title: step.popover.title,
          description: step.popover.description,
        },
      })),
      onNextClick: () => this.handleNext(),
      onPrevClick: () => this.handlePrev(),
      onDestroyStarted: () => this.stop(),
      onPopoverRender: popover => this.promoteToTopLayer(popover.wrapper),
    });

    this.driverInstance.drive(0);
  }

  private promoteToTopLayer(el: HTMLElement): void {
    if (typeof (el as HTMLElement & { showPopover?: () => void }).showPopover !== 'function') return;
    try {
      if (!el.hasAttribute('popover')) el.setAttribute('popover', 'manual');
      (el as HTMLElement & { showPopover: () => void }).showPopover();
    } catch {}
  }

  private handleNext(): void {
    if (!this.currentTour || !this.driverInstance) return;
    if (this._isProcessing()) return;

    const current = this.currentTour.steps[this._currentStepIndex()];
    const nextIndex = this._currentStepIndex() + 1;
    const isLast = nextIndex >= this.currentTour.steps.length;

    this._isProcessing.set(true);
    this.runAction$(current.onNextActionKey)
      .pipe(
        switchMap(() => (isLast ? of(null) : this.prepareStep$(this.currentTour!.steps[nextIndex], nextIndex))),
        takeUntil(this.stop$),
      )
      .subscribe({
        next: () => {
          this._isProcessing.set(false);
          if (isLast) {
            this.stop();
            return;
          }
          this._currentStepIndex.set(nextIndex);
          this.driverInstance?.moveTo(nextIndex);
        },
        error: err => {
          console.warn('[ProductTour] Failed to advance to next step:', err);
          this._isProcessing.set(false);
          this.stop();
        },
      });
  }

  private handlePrev(): void {
    if (!this.currentTour || !this.driverInstance) return;
    if (this._isProcessing()) return;

    const prevIndex = this._currentStepIndex() - 1;
    if (prevIndex < 0) return;

    this._isProcessing.set(true);
    this.prepareStep$(this.currentTour.steps[prevIndex], prevIndex, true)
      .pipe(takeUntil(this.stop$))
      .subscribe({
        next: () => {
          this._isProcessing.set(false);
          this._currentStepIndex.set(prevIndex);
          this.driverInstance?.moveTo(prevIndex);
        },
        error: err => {
          console.warn('[ProductTour] Failed to move to previous step:', err);
          this._isProcessing.set(false);
        },
      });
  }

  private prepareStep$(step: TourStep, index: number, backwards = false): Observable<void> {
    return this.runAction$(step.preNavigateActionKey).pipe(
      switchMap(() => this.navigate$(step, index, backwards)),
      switchMap(() => this.openModal$(step, index, backwards)),
      switchMap(() => this.runAction$(step.actionKey)),
      switchMap(() => this.waitForTarget$(step)),
      map(() => void 0),
    );
  }

  private navigate$(step: TourStep, index: number, backwards: boolean): Observable<void> {
    const target = this.resolve(step.navigate) ?? (backwards ? this.inheritedNavigate(step, index) : undefined);
    if (!target || this.router.url === target) return of(void 0);
    return defer(() => {
      this.closeCurrentModal();
      return from(this.router.navigateByUrl(target)).pipe(map(() => void 0));
    });
  }

  private inheritedNavigate(step: TourStep, index: number): string | undefined {
    if (this.isTargetPresent(step)) return undefined;
    const steps = this.currentTour?.steps ?? [];
    for (let i = index - 1; i >= 0; i--) {
      const target = this.resolve(steps[i].navigate);
      if (target) return target;
    }
    return undefined;
  }

  private inheritedModal(step: TourStep, index: number): TourStep['openModal'] | undefined {
    if (step.openModal) return step.openModal;
    if (step.navigate || step.preNavigateActionKey === PRODUCT_TOUR_CLOSE_MODAL_ACTION) return undefined;
    const steps = this.currentTour?.steps ?? [];
    for (let i = index - 1; i >= 0; i--) {
      if (steps[i].openModal) return steps[i].openModal;
      if (steps[i].navigate || steps[i].preNavigateActionKey === PRODUCT_TOUR_CLOSE_MODAL_ACTION) return undefined;
    }
    return undefined;
  }

  private isTargetPresent(step: TourStep): boolean {
    const selector = this.resolve(step.waitForElement)?.selector ?? this.resolve(step.element);
    return !selector || !!document.querySelector(selector);
  }

  private closeCurrentModal(): void {
    if (!this.currentModalRef) return;
    this.modalClosedByTour = true;
    this.currentModalRef.close();
    this.currentModalRef = null;
    this.currentModalComponent = null;
  }

  public closeActiveModal(): void {
    this.closeCurrentModal();
  }

  private openModal$(step: TourStep, index: number, backwards: boolean): Observable<void> {
    const modalConfig = backwards ? this.inheritedModal(step, index) : step.openModal;
    if (!modalConfig) {
      if (backwards) this.closeCurrentModal();
      return of(void 0);
    }
    if (backwards && this.currentModalComponent === modalConfig.component) return of(void 0);
    return defer(() => {
      this.openModalForStep(modalConfig.component, this.resolve(modalConfig.data));
      return of(void 0);
    });
  }

  private waitForTarget$(step: TourStep): Observable<void> {
    const waitConfig = this.resolve(step.waitForElement);
    const selector = waitConfig?.selector ?? this.resolve(step.element);
    if (!selector) return of(void 0);
    const timeoutMs = waitConfig?.timeoutMs ?? PRODUCT_TOUR_DEFAULT_WAIT_TIMEOUT_MS;
    const maxTicks = Math.ceil(timeoutMs / PRODUCT_TOUR_POLL_INTERVAL_MS);

    return timer(0, PRODUCT_TOUR_POLL_INTERVAL_MS).pipe(
      take(maxTicks + 1),
      map((_, i) => ({ el: document.querySelector(selector), tick: i })),
      filter(({ el, tick }) => el !== null || tick >= maxTicks),
      take(1),
      map(({ el }) => {
        if (!el) throw new Error(`[ProductTour] Element "${selector}" not found within ${timeoutMs}ms`);
      }),
    );
  }

  private resolve<T>(value: TourValue<T> | undefined): T | undefined {
    if (value === undefined) return undefined;
    if (typeof value === 'function') return (value as (ctx: TourContext) => T)(this.currentContext);
    return value;
  }

  private runAction$(key: string | undefined): Observable<void> {
    if (!key) return of(void 0);
    const fn = this.actions.get(key);
    if (!fn) {
      console.warn(`[ProductTour] Action "${key}" is not registered`);
      return of(void 0);
    }
    return defer(() => {
      const result = fn();
      if (result instanceof Promise) return from(result).pipe(map(() => void 0));
      if (isObservable(result)) return (result as Observable<unknown>).pipe(map(() => void 0));
      return of(void 0);
    });
  }

  private openModalForStep(component: Parameters<ModalService['open']>[0], data: unknown): void {
    this.closeCurrentModal();

    this.modalClosedByTour = false;
    this.currentModalComponent = component;
    this.currentModalRef = this.modalService.open(component, { data } as never);
    this.currentModalRef
      .afterClosed()
      .pipe(takeUntil(this.stop$))
      .subscribe(() => {
        const closedExternally = !this.modalClosedByTour;
        this.currentModalRef = null;
        this.currentModalComponent = null;
        this.modalClosedByTour = false;
        if (closedExternally && this._isActive()) this.stop();
      });
  }
}
